import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Video redaction overlay — sits absolutely over the <video> element.
 *
 * Two jobs:
 *  1. LIVE PREVIEW: for every video redaction active at the playhead,
 *     draw a div over its region — `backdrop-filter: blur()` for blur
 *     (genuinely blurs the video pixels beneath), solid black for
 *     boxes/full-frame. What you see is what the render burns in.
 *  2. DRAW MODE: crosshair drag captures a normalized bbox (relative to
 *     the CONTENT rect, not the element — videos letterbox inside the
 *     element box, so bars are excluded by the same math used to place
 *     preview boxes).
 *
 * All geometry is normalized [0,1] of the frame (BoxClipLayer
 * convention), matching what the ffmpeg filter graph consumes.
 */

function contentRect(videoEl) {
  const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
  const ew = videoEl.clientWidth, eh = videoEl.clientHeight;
  if (!vw || !vh || !ew || !eh) return null;
  const scale = Math.min(ew / vw, eh / vh);
  const cw = vw * scale, ch = vh * scale;
  return { left: (ew - cw) / 2, top: (eh - ch) / 2, width: cw, height: ch };
}

export default function VideoRedactionOverlay({
  mediaRef, items, currentTime, previewOn, drawing, drawStyle, onCapture,
}) {
  const [rect, setRect] = useState(null);
  const [draft, setDraft] = useState(null); // {x0,y0,x1,y1} px within overlay
  const overlayRef = useRef(null);

  const refresh = useCallback(() => {
    const el = mediaRef.current;
    setRect(el ? contentRect(el) : null);
  }, [mediaRef]);

  useEffect(() => {
    refresh();
    const el = mediaRef.current;
    if (!el) return;
    el.addEventListener('loadedmetadata', refresh);
    window.addEventListener('resize', refresh);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(refresh) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('loadedmetadata', refresh);
      window.removeEventListener('resize', refresh);
      ro?.disconnect();
    };
  }, [mediaRef, refresh]);

  const onMouseDown = (e) => {
    if (!drawing || !rect || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const o = overlayRef.current.getBoundingClientRect();
    const px = (me) => ({ x: me.clientX - o.left, y: me.clientY - o.top });
    const p0 = px(e);
    setDraft({ x0: p0.x, y0: p0.y, x1: p0.x, y1: p0.y });
    const onMove = (me) => {
      const p = px(me);
      setDraft(d => d && { ...d, x1: p.x, y1: p.y });
    };
    const onUp = (me) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setDraft(null);
      const p1 = px(me);
      const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
      const lx = clamp(Math.min(p0.x, p1.x), rect.left, rect.left + rect.width);
      const ly = clamp(Math.min(p0.y, p1.y), rect.top, rect.top + rect.height);
      const hx = clamp(Math.max(p0.x, p1.x), rect.left, rect.left + rect.width);
      const hy = clamp(Math.max(p0.y, p1.y), rect.top, rect.top + rect.height);
      if (hx - lx < 8 || hy - ly < 8) return; // accidental click
      onCapture?.([
        (lx - rect.left) / rect.width,
        (ly - rect.top) / rect.height,
        (hx - lx) / rect.width,
        (hy - ly) / rect.height,
      ]);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!rect) return null;

  const active = previewOn
    ? items.filter(r =>
        (r.kind === 'video_region' || r.kind === 'video_frame')
        && r.status === 'active'  // pending suggestions are review-only
        && currentTime >= r.time_start && currentTime < r.time_end)
    : [];

  const boxStyle = (r) => {
    if (r.kind === 'video_frame') {
      return {
        left: rect.left, top: rect.top, width: rect.width, height: rect.height,
        background: '#000',
      };
    }
    const [x, y, w, h] = r.region?.bbox || [0, 0, 0, 0];
    const base = {
      left: rect.left + x * rect.width,
      top: rect.top + y * rect.height,
      width: w * rect.width,
      height: h * rect.height,
    };
    return (r.region?.style === 'black')
      ? { ...base, background: '#000' }
      : { ...base, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' };
  };

  return (
    <div
      ref={overlayRef}
      className={`rdx-video-overlay ${drawing ? 'rdx-video-overlay--drawing' : ''}`}
      onMouseDown={onMouseDown}
    >
      {active.map(r => (
        <div key={r.id} className="rdx-video-box" style={boxStyle(r)} />
      ))}
      {draft && (
        <div
          className="rdx-video-draft"
          style={{
            left: Math.min(draft.x0, draft.x1),
            top: Math.min(draft.y0, draft.y1),
            width: Math.abs(draft.x1 - draft.x0),
            height: Math.abs(draft.y1 - draft.y0),
            background: drawStyle === 'black'
              ? 'rgba(0,0,0,0.55)' : 'rgba(52,152,219,0.25)',
          }}
        />
      )}
      {drawing && (
        <div className="rdx-video-drawhint">
          Drag a box over what to {drawStyle === 'black' ? 'black out' : 'blur'} · Esc to cancel
        </div>
      )}
    </div>
  );
}
